import {Action, FormControl} from 'platform/components';

import {useMemo} from 'react';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';
import {useTireSetActions} from './useTireSetActions';

interface UseExcludedTireSetActionsParams extends TestIdProps {
  tireOrderId: string;
  tireSetId: string;
  control: FormControl<TireSetDetailFormDefinition>;
  canEditTireSet: boolean;
  isTiresSetLabelLoading: boolean;
  isTiresSetDeliveryNoteLoading: boolean;
  homeBranchId: string | Nullish;
  transferToOrder: () => void;
  printLabel: (wheelIds: string[]) => void;
  printDeliveryNote: (wheelIds: string[]) => void;
}

export function useExcludedTireSetActions(params: UseExcludedTireSetActionsParams) {
  const {
    tireOrderId,
    tireSetId,
    control,
    canEditTireSet,
    isTiresSetLabelLoading,
    isTiresSetDeliveryNoteLoading,
    transferToOrder,
    printLabel,
    printDeliveryNote,
    homeBranchId,
    ...testIdProps
  } = params;

  const {availableActions, getCheckedWheelIds} = useTireSetActions({
    tireOrderId,
    tireSetId,
    control,
    includedKeys: ['transferToOrder', 'printLabel', 'printDeliveryNote'],
    homeBranchId,
  });

  const actions: Action[] = useMemo(
    () =>
      availableActions.map((action: {key: string}) => {
        const baseAction = {
          type: 'buttonWithTooltip' as const,
          leftIcon:
            action.key === 'transferToOrder' ? 'sidebar/workshop_service_orders' : 'action/print',
          variant: 'link' as const,
          isDisabled: !canEditTireSet,
          'data-testid': suffixTestId(action.key, testIdProps),
          tooltip: {
            label: !canEditTireSet ? i18n.t('general.labels.noPermission') : undefined,
          },
        } as const;

        switch (action.key) {
          case 'transferToOrder':
            return {
              ...baseAction,
              title: i18n.t('entity.tireSet.actions.transferToOrder'),
              onClick: transferToOrder,
            };
          case 'printLabel':
            return {
              ...baseAction,
              title: i18n.t('entity.tireSet.actions.printLabel'),
              isLoading: isTiresSetLabelLoading,
              onClick: () => printLabel(getCheckedWheelIds()),
            };
          case 'printDeliveryNote':
            return {
              ...baseAction,
              title: i18n.t('entity.tireSet.actions.printDeliveryNote'),
              isLoading: isTiresSetDeliveryNoteLoading,
              onClick: () => printDeliveryNote(getCheckedWheelIds()),
            };
          default:
            return {
              ...baseAction,
              title: action.key,
              onClick: () => {},
            };
        }
      }),
    [
      availableActions,
      canEditTireSet,
      isTiresSetLabelLoading,
      isTiresSetDeliveryNoteLoading,
      transferToOrder,
      printLabel,
      printDeliveryNote,
      getCheckedWheelIds,
      testIdProps,
    ]
  );

  return {actions};
}
